/**
 * Auto-generated service - DO NOT EDIT MANUALLY
 * Generated from: webhook.ts
 * Run: pnpm gen
 */

import { API_METHODS } from '../constants/api-methods';
import { getApiPath } from '../path';
import { type RequestOptions, type Transport } from '../transport';

import type {
  AdminBulkRetryWebhookDeliveriesRequest,
  AdminBulkRetryWebhookDeliveriesResponse,
  AdminCreateWebhookEndpointRequest,
  AdminCreateWebhookEndpointResponse,
  AdminDeleteWebhookEndpointRequest,
  AdminDeleteWebhookEndpointResponse,
  AdminDisableWebhookEndpointRequest,
  AdminDisableWebhookEndpointResponse,
  AdminGetWebhookDeliveryRequest,
  AdminGetWebhookDeliveryResponse,
  AdminGetWebhookEndpointHealthRequest,
  AdminGetWebhookEndpointHealthResponse,
  AdminGetWebhookEndpointRequest,
  AdminGetWebhookEndpointResponse,
  AdminGetWebhookOperationRequest,
  AdminGetWebhookOperationResponse,
  AdminListWebhookDeliveriesRequest,
  AdminListWebhookDeliveriesResponse,
  AdminListWebhookEndpointsRequest,
  AdminListWebhookEndpointsResponse,
  AdminPauseWebhookEndpointRequest,
  AdminPauseWebhookEndpointResponse,
  AdminReplayWebhookWindowRequest,
  AdminReplayWebhookWindowResponse,
  AdminResumeWebhookEndpointRequest,
  AdminResumeWebhookEndpointResponse,
  AdminRetryWebhookDeliveryRequest,
  AdminRetryWebhookDeliveryResponse,
  AdminRotateWebhookEndpointSecretRequest,
  AdminRotateWebhookEndpointSecretResponse,
  AdminSendWebhookTestEventRequest,
  AdminSendWebhookTestEventResponse,
  AdminUpdateWebhookEndpointRequest,
  AdminUpdateWebhookEndpointResponse,
  BulkRetryWebhookDeliveriesRequest,
  BulkRetryWebhookDeliveriesResponse,
  CreateWebhookEndpointRequest,
  CreateWebhookEndpointResponse,
  DeleteWebhookEndpointRequest,
  DeleteWebhookEndpointResponse,
  DisableWebhookEndpointRequest,
  DisableWebhookEndpointResponse,
  GetWebhookDeliveryRequest,
  GetWebhookDeliveryResponse,
  GetWebhookEndpointHealthRequest,
  GetWebhookEndpointHealthResponse,
  GetWebhookEndpointRequest,
  GetWebhookEndpointResponse,
  GetWebhookOperationRequest,
  GetWebhookOperationResponse,
  ListWebhookDeliveriesRequest,
  ListWebhookDeliveriesResponse,
  ListWebhookEndpointsRequest,
  ListWebhookEndpointsResponse,
  PauseWebhookEndpointRequest,
  PauseWebhookEndpointResponse,
  ReplayWebhookWindowRequest,
  ReplayWebhookWindowResponse,
  ResumeWebhookEndpointRequest,
  ResumeWebhookEndpointResponse,
  RetryWebhookDeliveryRequest,
  RetryWebhookDeliveryResponse,
  RotateWebhookEndpointSecretRequest,
  RotateWebhookEndpointSecretResponse,
  SendWebhookTestEventRequest,
  SendWebhookTestEventResponse,
  UpdateWebhookEndpointRequest,
  UpdateWebhookEndpointResponse,
} from '../types/webhook';

export const createWebhookService = (transport: Transport) => ({
  async listWebhookEndpoints(
    request: ListWebhookEndpointsRequest,
    options: RequestOptions = {},
  ): Promise<ListWebhookEndpointsResponse> {
    const { organization_id, ...params } = request;
    const url = getApiPath(API_METHODS.WebhookService.ListWebhookEndpoints, { organization_id });
    return transport.get<ListWebhookEndpointsResponse>(url, { params, ...options });
  },

  async createWebhookEndpoint(
    request: CreateWebhookEndpointRequest,
    options: RequestOptions = {},
  ): Promise<CreateWebhookEndpointResponse> {
    const { organization_id, ...body } = request;
    const url = getApiPath(API_METHODS.WebhookService.CreateWebhookEndpoint, { organization_id });
    return transport.post<CreateWebhookEndpointResponse>(url, body, options);
  },

  async getWebhookEndpoint(
    request: GetWebhookEndpointRequest,
    options: RequestOptions = {},
  ): Promise<GetWebhookEndpointResponse> {
    const { organization_id, endpoint_id } = request;
    const url = getApiPath(API_METHODS.WebhookService.GetWebhookEndpoint, {
      organization_id,
      endpoint_id,
    });
    return transport.get<GetWebhookEndpointResponse>(url, options);
  },

  async updateWebhookEndpoint(
    request: UpdateWebhookEndpointRequest,
    options: RequestOptions = {},
  ): Promise<UpdateWebhookEndpointResponse> {
    const { organization_id, endpoint_id, ...body } = request;
    const url = getApiPath(API_METHODS.WebhookService.UpdateWebhookEndpoint, {
      organization_id,
      endpoint_id,
    });
    return transport.post<UpdateWebhookEndpointResponse>(url, body, options);
  },

  async deleteWebhookEndpoint(
    request: DeleteWebhookEndpointRequest,
    options: RequestOptions = {},
  ): Promise<DeleteWebhookEndpointResponse> {
    const { organization_id, endpoint_id } = request;
    const url = getApiPath(API_METHODS.WebhookService.DeleteWebhookEndpoint, {
      organization_id,
      endpoint_id,
    });
    return transport.delete<DeleteWebhookEndpointResponse>(url, options);
  },

  async pauseWebhookEndpoint(
    request: PauseWebhookEndpointRequest,
    options: RequestOptions = {},
  ): Promise<PauseWebhookEndpointResponse> {
    const { organization_id, endpoint_id } = request;
    const url = getApiPath(API_METHODS.WebhookService.PauseWebhookEndpoint, {
      organization_id,
      endpoint_id,
    });
    return transport.post<PauseWebhookEndpointResponse>(url, {}, options);
  },

  async disableWebhookEndpoint(
    request: DisableWebhookEndpointRequest,
    options: RequestOptions = {},
  ): Promise<DisableWebhookEndpointResponse> {
    const { organization_id, endpoint_id } = request;
    const url = getApiPath(API_METHODS.WebhookService.DisableWebhookEndpoint, {
      organization_id,
      endpoint_id,
    });
    return transport.post<DisableWebhookEndpointResponse>(url, {}, options);
  },

  async resumeWebhookEndpoint(
    request: ResumeWebhookEndpointRequest,
    options: RequestOptions = {},
  ): Promise<ResumeWebhookEndpointResponse> {
    const { organization_id, endpoint_id } = request;
    const url = getApiPath(API_METHODS.WebhookService.ResumeWebhookEndpoint, {
      organization_id,
      endpoint_id,
    });
    return transport.post<ResumeWebhookEndpointResponse>(url, {}, options);
  },

  async rotateWebhookEndpointSecret(
    request: RotateWebhookEndpointSecretRequest,
    options: RequestOptions = {},
  ): Promise<RotateWebhookEndpointSecretResponse> {
    const { organization_id, endpoint_id } = request;
    const url = getApiPath(API_METHODS.WebhookService.RotateWebhookEndpointSecret, {
      organization_id,
      endpoint_id,
    });
    return transport.post<RotateWebhookEndpointSecretResponse>(url, {}, options);
  },

  async sendWebhookTestEvent(
    request: SendWebhookTestEventRequest,
    options: RequestOptions = {},
  ): Promise<SendWebhookTestEventResponse> {
    const { organization_id, endpoint_id } = request;
    const url = getApiPath(API_METHODS.WebhookService.SendWebhookTestEvent, {
      organization_id,
      endpoint_id,
    });
    return transport.post<SendWebhookTestEventResponse>(url, {}, options);
  },

  async replayWebhookWindow(
    request: ReplayWebhookWindowRequest,
    options: RequestOptions = {},
  ): Promise<ReplayWebhookWindowResponse> {
    const { organization_id, endpoint_id, ...body } = request;
    const url = getApiPath(API_METHODS.WebhookService.ReplayWebhookWindow, {
      organization_id,
      endpoint_id,
    });
    return transport.post<ReplayWebhookWindowResponse>(url, body, options);
  },

  async getWebhookEndpointHealth(
    request: GetWebhookEndpointHealthRequest,
    options: RequestOptions = {},
  ): Promise<GetWebhookEndpointHealthResponse> {
    const { organization_id, ...params } = request;
    const url = getApiPath(API_METHODS.WebhookService.GetWebhookEndpointHealth, {
      organization_id,
    });
    return transport.get<GetWebhookEndpointHealthResponse>(url, { params, ...options });
  },

  async listWebhookDeliveries(
    request: ListWebhookDeliveriesRequest,
    options: RequestOptions = {},
  ): Promise<ListWebhookDeliveriesResponse> {
    const { organization_id, ...params } = request;
    const url = getApiPath(API_METHODS.WebhookService.ListWebhookDeliveries, { organization_id });
    return transport.get<ListWebhookDeliveriesResponse>(url, { params, ...options });
  },

  async bulkRetryWebhookDeliveries(
    request: BulkRetryWebhookDeliveriesRequest,
    options: RequestOptions = {},
  ): Promise<BulkRetryWebhookDeliveriesResponse> {
    const { organization_id, ...body } = request;
    const url = getApiPath(API_METHODS.WebhookService.BulkRetryWebhookDeliveries, {
      organization_id,
    });
    return transport.post<BulkRetryWebhookDeliveriesResponse>(url, body, options);
  },

  async getWebhookDelivery(
    request: GetWebhookDeliveryRequest,
    options: RequestOptions = {},
  ): Promise<GetWebhookDeliveryResponse> {
    const { organization_id, delivery_id } = request;
    const url = getApiPath(API_METHODS.WebhookService.GetWebhookDelivery, {
      organization_id,
      delivery_id,
    });
    return transport.get<GetWebhookDeliveryResponse>(url, options);
  },

  async retryWebhookDelivery(
    request: RetryWebhookDeliveryRequest,
    options: RequestOptions = {},
  ): Promise<RetryWebhookDeliveryResponse> {
    const { organization_id, delivery_id } = request;
    const url = getApiPath(API_METHODS.WebhookService.RetryWebhookDelivery, {
      organization_id,
      delivery_id,
    });
    return transport.post<RetryWebhookDeliveryResponse>(url, {}, options);
  },

  async getWebhookOperation(
    request: GetWebhookOperationRequest,
    options: RequestOptions = {},
  ): Promise<GetWebhookOperationResponse> {
    const { organization_id, operation_id } = request;
    const url = getApiPath(API_METHODS.WebhookService.GetWebhookOperation, {
      organization_id,
      operation_id,
    });
    return transport.get<GetWebhookOperationResponse>(url, options);
  },

  async adminListWebhookEndpoints(
    request: AdminListWebhookEndpointsRequest,
    options: RequestOptions = {},
  ): Promise<AdminListWebhookEndpointsResponse> {
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminListWebhookEndpoints);
    return transport.get<AdminListWebhookEndpointsResponse>(url, { params: request, ...options });
  },

  async adminCreateWebhookEndpoint(
    request: AdminCreateWebhookEndpointRequest,
    options: RequestOptions = {},
  ): Promise<AdminCreateWebhookEndpointResponse> {
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminCreateWebhookEndpoint);
    return transport.post<AdminCreateWebhookEndpointResponse>(url, request, options);
  },

  async adminGetWebhookEndpoint(
    request: AdminGetWebhookEndpointRequest,
    options: RequestOptions = {},
  ): Promise<AdminGetWebhookEndpointResponse> {
    const { endpoint_id } = request;
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminGetWebhookEndpoint, {
      endpoint_id,
    });
    return transport.get<AdminGetWebhookEndpointResponse>(url, options);
  },

  async adminUpdateWebhookEndpoint(
    request: AdminUpdateWebhookEndpointRequest,
    options: RequestOptions = {},
  ): Promise<AdminUpdateWebhookEndpointResponse> {
    const { endpoint_id, ...body } = request;
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminUpdateWebhookEndpoint, {
      endpoint_id,
    });
    return transport.post<AdminUpdateWebhookEndpointResponse>(url, body, options);
  },

  async adminDeleteWebhookEndpoint(
    request: AdminDeleteWebhookEndpointRequest,
    options: RequestOptions = {},
  ): Promise<AdminDeleteWebhookEndpointResponse> {
    const { endpoint_id } = request;
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminDeleteWebhookEndpoint, {
      endpoint_id,
    });
    return transport.delete<AdminDeleteWebhookEndpointResponse>(url, options);
  },

  async adminPauseWebhookEndpoint(
    request: AdminPauseWebhookEndpointRequest,
    options: RequestOptions = {},
  ): Promise<AdminPauseWebhookEndpointResponse> {
    const { endpoint_id } = request;
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminPauseWebhookEndpoint, {
      endpoint_id,
    });
    return transport.post<AdminPauseWebhookEndpointResponse>(url, {}, options);
  },

  async adminDisableWebhookEndpoint(
    request: AdminDisableWebhookEndpointRequest,
    options: RequestOptions = {},
  ): Promise<AdminDisableWebhookEndpointResponse> {
    const { endpoint_id } = request;
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminDisableWebhookEndpoint, {
      endpoint_id,
    });
    return transport.post<AdminDisableWebhookEndpointResponse>(url, {}, options);
  },

  async adminResumeWebhookEndpoint(
    request: AdminResumeWebhookEndpointRequest,
    options: RequestOptions = {},
  ): Promise<AdminResumeWebhookEndpointResponse> {
    const { endpoint_id } = request;
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminResumeWebhookEndpoint, {
      endpoint_id,
    });
    return transport.post<AdminResumeWebhookEndpointResponse>(url, {}, options);
  },

  async adminRotateWebhookEndpointSecret(
    request: AdminRotateWebhookEndpointSecretRequest,
    options: RequestOptions = {},
  ): Promise<AdminRotateWebhookEndpointSecretResponse> {
    const { endpoint_id } = request;
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminRotateWebhookEndpointSecret, {
      endpoint_id,
    });
    return transport.post<AdminRotateWebhookEndpointSecretResponse>(url, {}, options);
  },

  async adminSendWebhookTestEvent(
    request: AdminSendWebhookTestEventRequest,
    options: RequestOptions = {},
  ): Promise<AdminSendWebhookTestEventResponse> {
    const { endpoint_id } = request;
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminSendWebhookTestEvent, {
      endpoint_id,
    });
    return transport.post<AdminSendWebhookTestEventResponse>(url, {}, options);
  },

  async adminReplayWebhookWindow(
    request: AdminReplayWebhookWindowRequest,
    options: RequestOptions = {},
  ): Promise<AdminReplayWebhookWindowResponse> {
    const { endpoint_id, ...body } = request;
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminReplayWebhookWindow, {
      endpoint_id,
    });
    return transport.post<AdminReplayWebhookWindowResponse>(url, body, options);
  },

  async adminGetWebhookEndpointHealth(
    request: AdminGetWebhookEndpointHealthRequest,
    options: RequestOptions = {},
  ): Promise<AdminGetWebhookEndpointHealthResponse> {
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminGetWebhookEndpointHealth);
    return transport.get<AdminGetWebhookEndpointHealthResponse>(url, {
      params: request,
      ...options,
    });
  },

  async adminListWebhookDeliveries(
    request: AdminListWebhookDeliveriesRequest,
    options: RequestOptions = {},
  ): Promise<AdminListWebhookDeliveriesResponse> {
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminListWebhookDeliveries);
    return transport.get<AdminListWebhookDeliveriesResponse>(url, { params: request, ...options });
  },

  async adminBulkRetryWebhookDeliveries(
    request: AdminBulkRetryWebhookDeliveriesRequest,
    options: RequestOptions = {},
  ): Promise<AdminBulkRetryWebhookDeliveriesResponse> {
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminBulkRetryWebhookDeliveries);
    return transport.post<AdminBulkRetryWebhookDeliveriesResponse>(url, request, options);
  },

  async adminGetWebhookDelivery(
    request: AdminGetWebhookDeliveryRequest,
    options: RequestOptions = {},
  ): Promise<AdminGetWebhookDeliveryResponse> {
    const { delivery_id } = request;
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminGetWebhookDelivery, {
      delivery_id,
    });
    return transport.get<AdminGetWebhookDeliveryResponse>(url, options);
  },

  async adminRetryWebhookDelivery(
    request: AdminRetryWebhookDeliveryRequest,
    options: RequestOptions = {},
  ): Promise<AdminRetryWebhookDeliveryResponse> {
    const { delivery_id } = request;
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminRetryWebhookDelivery, {
      delivery_id,
    });
    return transport.post<AdminRetryWebhookDeliveryResponse>(url, {}, options);
  },

  async adminGetWebhookOperation(
    request: AdminGetWebhookOperationRequest,
    options: RequestOptions = {},
  ): Promise<AdminGetWebhookOperationResponse> {
    const { operation_id } = request;
    const url = getApiPath(API_METHODS.AdminWebhookService.AdminGetWebhookOperation, {
      operation_id,
    });
    return transport.get<AdminGetWebhookOperationResponse>(url, options);
  },
});

export type WebhookService = ReturnType<typeof createWebhookService>;
